import React, { useState } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import { Hotel, Lightbulb, FileText, LogOut } from 'lucide-react';
import { cn } from '../utils/cn';
import { Modal, Button, Tooltip } from './ui';
import {
  StyledLogoutConfirmation,
  StyledConfirmationText,
  StyledActionButtons,
  StyledConfirmationContent,
} from './Sidebar/LogoutConfirmation.styled';
import Logo from '../assets/logo.png';

// Icon mapping for RFP navigation
const iconMap = {
  Hotel,
  Lightbulb,
  FileText,
  LogOut,
} as const;

type IconName = keyof typeof iconMap;

// Styled component variants (same as Sidebar)
const sidebarVariants = {
  default: 'sidebar-default',
  dark: 'sidebar-dark',
  minimal: 'sidebar-minimal',
} as const;

const sizeVariants = {
  normal: '',
  wide: 'sidebar-wide',
} as const;

const stateVariants = {
  expanded: 'sidebar-expanded',
  collapsed: 'sidebar-collapsed',
} as const;

const logoVariants = {
  expanded: 'sidebar-logo-expanded',
  collapsed: 'sidebar-logo-collapsed',
} as const;

export interface RFPSidebarProps {
  isOpen: boolean;
  variant?: keyof typeof sidebarVariants;
  size?: keyof typeof sizeVariants;
  className?: string;
  mobileOpen?: boolean;
  onLinkClick?: (path: string) => void;
}

const RFPSidebar: React.FC<RFPSidebarProps> = ({
  isOpen,
  variant = 'default',
  size = 'normal',
  className,
  mobileOpen = false,
  onLinkClick,
}) => {
  const { rfpLinkId } = useParams<{ rfpLinkId: string }>();

  const rfpNavigation = [
    {
      to: `/rfp/${rfpLinkId}`,
      text: 'Hotel',
      icon: 'Hotel',
      isExternal: false,
    },
    {
      to: `/rfp/${rfpLinkId}/our-approach`,
      text: 'Our Approach',
      icon: 'Lightbulb',
      isExternal: false,
    },
    {
      to: '/',
      text: 'Portal',
      icon: 'FileText',
      isExternal: true,
    },
  ];

  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);

  const renderIcon = (iconName: string, size: number = 20) => {
    const IconComponent = iconMap[iconName as IconName];
    return IconComponent ? <IconComponent size={size} /> : null;
  };

  const handleLinkClick = (path: string) => {
    onLinkClick?.(path);
  };

  const handleLogout = () => {
    // For now, just redirect to home or login
    window.location.href = '/';
  };

  const handleConfirmLogout = () => {
    handleLogout();
    setIsLogoutModalOpen(false);
  };

  const handleCancelLogout = () => {
    setIsLogoutModalOpen(false);
  };

  return (
    <aside
      className={cn(
        'sidebar-base',
        sidebarVariants[variant],
        isOpen ? stateVariants.expanded : stateVariants.collapsed,
        sizeVariants[size],
        mobileOpen && 'sidebar-mobile-open',
        className
      )}
    >
      {/* Logo Section */}
      <div
        className={`sidebar-logo-container ${
          !isOpen && 'sidebar-logo-container-collapsed'
        }`}
      >
        <img
          src={Logo}
          alt="Alō Index"
          className={cn(
            isOpen ? logoVariants.expanded : logoVariants.collapsed
          )}
        />
      </div>

      {/* Navigation Section */}
      <nav
        className="sidebar-nav"
        role="navigation"
        aria-label="RFP navigation"
      >
        {rfpNavigation.map(link =>
          link.isExternal ? (
            isOpen ? (
              <a
                key={link.to}
                href={link.to}
                target="_blank"
                rel="noopener noreferrer"
                className="sidebar-nav-link"
                aria-label={link.text}
              >
                <div className="sidebar-nav-icon" aria-hidden="true">
                  {renderIcon(link.icon)}
                </div>
                {isOpen && <span className="sidebar-nav-text">{link.text}</span>}
              </a>
            ) : (
              <Tooltip
                key={link.to}
                content={link.text}
                position="right"
                iconClassName="!flex"
                icon={
                  <a
                    href={link.to}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="sidebar-nav-link"
                    aria-label={link.text}
                  >
                    <div className="sidebar-nav-icon" aria-hidden="true">
                      {renderIcon(link.icon)}
                    </div>
                  </a>
                }
              />
            )
          ) : isOpen ? (
            <NavLink
              key={link.to}
              to={link.to}
              end
              onClick={() => handleLinkClick(link.to)}
              className={({ isActive }) =>
                cn('sidebar-nav-link', isActive && 'sidebar-nav-link-active')
              }
              aria-label={link.text}
            >
              <div className="sidebar-nav-icon" aria-hidden="true">
                {renderIcon(link.icon)}
              </div>
              {isOpen && <span className="sidebar-nav-text">{link.text}</span>}
            </NavLink>
          ) : (
            <Tooltip
              key={link.to}
              content={link.text}
              position="right"
              iconClassName="!flex"
              icon={
                <NavLink
                  to={link.to}
                  end
                  onClick={() => handleLinkClick(link.to)}
                  className={({ isActive }) =>
                    cn(
                      'sidebar-nav-link',
                      isActive && 'sidebar-nav-link-active'
                    )
                  }
                  aria-label={link.text}
                >
                  <div className="sidebar-nav-icon" aria-hidden="true">
                    {renderIcon(link.icon)}
                  </div>
                  {isOpen && (
                    <span className="sidebar-nav-text">{link.text}</span>
                  )}
                </NavLink>
              }
            />
          )
        )}
      </nav>

      {/* Logout Section at Bottom */}
      {/* <div className="sidebar-logout-container" style={{ marginTop: 'auto' }}>
        <button
          onClick={() => setIsLogoutModalOpen(true)}
          className="sidebar-logout-button"
          title={!isOpen ? 'Log out' : undefined}
          aria-label="Log out"
        >
          <div className="sidebar-nav-icon" aria-hidden="true">
            {renderIcon('LogOut')}
          </div>
          {isOpen && <span className="sidebar-nav-text">Log out</span>}
        </button>
      </div> */}

      {/* Logout Confirmation Modal */}
      <Modal
        isOpen={isLogoutModalOpen}
        onClose={handleCancelLogout}
        title="Confirm Log out"
        size="sm"
      >
        <StyledLogoutConfirmation>
          <StyledConfirmationContent>
            <h3>Are you sure you want to log out?</h3>
            <StyledConfirmationText>
              You will be redirected to the home page.
            </StyledConfirmationText>
          </StyledConfirmationContent>

          <StyledActionButtons>
            <Button variant="outline" onClick={handleCancelLogout}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleConfirmLogout}>
              Log out
            </Button>
          </StyledActionButtons>
        </StyledLogoutConfirmation>
      </Modal>
    </aside>
  );
};

export default RFPSidebar;
